"use client"

import { useState, useEffect } from "react"
import NewReleaseTable from "./NewReleaseTable"
import { saveAccessLog } from "../utils/commonUtils"
import api from "../api/api"
import { ChevronRight, FileText, Globe, BarChart3, Newspaper, Building2, ArrowRight, ChevronLeft } from "lucide-react"
import { useNavigate, useLocation } from "react-router-dom"
import NewReleaseFilter from "./NewReleaseFilter"

const NewReleaseSearch = ({
  onDashboardClick,
  selectedCompanies,
  onCompanyClick,
  addAllCompanies,
  addCompany,
  removeCompany,
  registeredCompanies,
  handleOpenAnalysis,
  handleSummaryClick,
  isMobile,
}) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [newReleaseData, setNewReleaseReleaseData] = useState({ data: null })
  const [newReleaseCountry, setNewReleaseCountry] = useState("JP")
  const [activeTab, setActiveTab] = useState("press-release")

  const [industries, setIndustries] = useState([])
  const [searchTerm, setSearchTerm] = useState("")
  const [titleSearchTerm, setTitleSearchTerm] = useState("") // タイトル検索用の状態を追加
  const [selectedIndustry, setSelectedIndustry] = useState()
  const [startDate, setStartDate] = useState("")
  const [endDate, setEndDate] = useState("")
  const [showFilters, setShowFilters] = useState(false)
  const [isFiltering, setIsFiltering] = useState(false)

  // ページネーション用の状態を追加
  const [currentPage, setCurrentPage] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const itemsPerPage = 100 // 1ページあたり100件表示

  const handleNewReleaseCountrySelect = (country) => {
    setNewReleaseCountry(country)
    setCurrentPage(1) // 国が変わったらページをリセット
  }

  const fetchNewReleaseData = async () => {
    try {
      setIsFiltering(true) // データ取得開始時にフィルタリング中フラグを立てる

      const response = await api.get(`${process.env.REACT_APP_API_URL}/chat/api/new-release-list/${activeTab}/`, {
        params: {
          country: newReleaseCountry,
          industry_fmp_id: selectedIndustry,
          date_from: startDate,
          date_to: endDate,
          company_name: searchTerm,
          title: titleSearchTerm,
          page: currentPage,
          page_size: itemsPerPage,
        },
      })

      setNewReleaseReleaseData({
        page: response.data.page,
        num: response.data.num_of_data,
        totalData: response.data.total_data,
        data: response.data.results,
        totalPages: response.data.total_pages,
      })

      // 総アイテム数と総ページ数を設定
      setTotalItems(response.data.total_data)
      setTotalPages(response.data.total_pages)
    } catch (error) {
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        // 未認証の場合、ログイン画面にリダイレクト
        navigate("/login", { state: { from: location.pathname } })
      } else {
        console.error("Error fetching first introduction:", error)
      }
    } finally {
      setIsFiltering(false) // データ取得完了時にフィルタリング中フラグを解除
    }
  }

  useEffect(() => {
    fetchNewReleaseData()
  }, [activeTab, newReleaseCountry, selectedIndustry, startDate, endDate, searchTerm, titleSearchTerm, currentPage]) // titleSearchTermを依存配列に追加

  useEffect(() => {
    saveAccessLog("analysis new release search")
  }, [])

  // フィルター変更時にページをリセット
  useEffect(() => {
    setCurrentPage(1)
  }, [activeTab, newReleaseCountry, selectedIndustry, startDate, endDate, searchTerm, titleSearchTerm]) // titleSearchTermを依存配列に追加

  const options = [
    { id: "JP", label: "日本" },
    { id: "US", label: "米国" },
  ]

  // カテゴリアイコンとラベルの設定
  const tabInfo = {
    "press-release": {
      icon: Newspaper,
      label: "プレスリリース",
      color: "bg-blue-500",
      textColor: "text-blue-500",
    },
    esg: {
      icon: Globe,
      label: "ESG情報",
      color: "bg-emerald-500",
      textColor: "text-emerald-500",
    },
    ma: {
      icon: Building2,
      label: "M&A情報",
      color: "bg-yellow-800",
      textColor: "text-yellow-800",
    },
    "timely-disclosure": {
      icon: FileText,
      label: "適時開示",
      color: "bg-amber-500",
      textColor: "text-amber-500",
    },
    "sec-report": {
      icon: BarChart3,
      label: "有価証券報告書",
      color: "bg-purple-500",
      textColor: "text-purple-500",
    },
  }

  // 業界の取得
  useEffect(() => {
    const fetchIndustries = async () => {
      try {
        const response = await api.get(`${process.env.REACT_APP_API_URL}/chat/api/industry/`, {
          params: { country: newReleaseCountry },
        })
        setIndustries(response.data)
      } catch (error) {
        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
          // 未認証の場合、ログイン画面にリダイレクト
          navigate("/login", { state: { from: location.pathname } })
        } else {
          console.error("Error fetching first introduction:", error)
        }
      }
    }
    fetchIndustries()
  }, [newReleaseCountry])

  // クラス名を条件付きで結合するヘルパー関数
  const cn = (...classes) => {
    return classes.filter(Boolean).join(" ")
  }

  const onCategoryClick = (type) => {
    setActiveTab(type)
    setCurrentPage(1) // カテゴリが変わったらページをリセット
  }

  // 検索実行関数を修正して、タイトル検索も含める
  const executeSearch = (companyName, title) => {
    setSearchTerm(companyName)
    setTitleSearchTerm(title)
    setCurrentPage(1) // 検索条件が変わったらページをリセット
    // fetchNewReleaseDataはuseEffectで自動的に呼び出される
  }

  // 前のページへ移動
  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1)
    }
  }

  // 次のページへ移動
  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1)
    }
  }

  // 現在表示中のアイテム範囲を計算
  const startItem = (currentPage - 1) * itemsPerPage + 1
  const endItem = Math.min(currentPage * itemsPerPage, totalItems)

  return (
    <div className={isMobile ? "" : "tabcontent-search"}>
      <div className={isMobile ? "" : "analysis-container"}>
        <div className={isMobile ? "py-2 px-3" : "py-[0px] px-[5px] md:px-[10px]"}>
          {/* タブ切り替え - スマホでは横スクロール可能 */}
          <div className="overflow-x-auto pb-2">
            <div className={`flex min-w-max md:min-w-0 ${isMobile ? "" : "md:grid md:grid-cols-5 md:gap-3"}`}>
              {Object.keys(tabInfo).map((type) => {
                const { icon: Icon, label, color, textColor } = tabInfo[type]
                const isActive = activeTab === type

                return (
                  <button
                    key={type}
                    onClick={() => onCategoryClick(type)}
                    className={cn(
                      "relative overflow-hidden rounded-xl px-3 md:px-4 my-1 md:my-2 mx-1 md:mx-0 text-left transition-all duration-200 h-[45px] md:h-[50px] flex-shrink-0",
                      isActive ? "ring-2 ring-offset-2" : "bg-gray-50 hover:bg-gray-100",
                      isActive ? `${color} text-white ring-offset-white` : "text-gray-700",
                    )}
                  >
                    <div className="flex items-center gap-2 md:gap-3">
                      <div className={cn("p-1 md:p-2 rounded-lg", isActive ? "bg-white/20" : `${color} bg-opacity-10`)}>
                        <Icon className={cn("h-4 w-4 md:h-5 md:w-5", isActive ? "text-white" : textColor)} />
                      </div>
                      <span className="text-xs md:text-sm font-medium">{label}</span>
                      {isActive && (
                        <div className="absolute right-1 md:right-3">
                          <ChevronRight className="h-4 w-4 md:h-5 md:w-5" />
                        </div>
                      )}
                    </div>
                  </button>
                )
              })}
            </div>
          </div>

          {/* フィルター部分 - タイトル検索のpropsを追加 */}
          <NewReleaseFilter
            industries={industries}
            setIndustries={setIndustries}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            titleSearchTerm={titleSearchTerm}
            setTitleSearchTerm={setTitleSearchTerm}
            selectedIndustry={selectedIndustry}
            setSelectedIndustry={setSelectedIndustry}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            showFilters={showFilters}
            setShowFilters={setShowFilters}
            twoColumnSwitchOptions={options}
            handleNewReleaseCountrySelect={handleNewReleaseCountrySelect}
            newReleaseCountry={newReleaseCountry}
            executeSearch={executeSearch}
            isMobile={isMobile}
            isFiltering={isFiltering}
            setIsFiltering={setIsFiltering}
          />
        </div>
        {/* 分析画面ボタン */}
        <div
          className={`flex justify-between ml-4 items-center ${selectedCompanies && selectedCompanies.length > 0 ? "mb-4" : "mb-0"}`}
        >
          {selectedCompanies &&
            selectedCompanies.length > 0 &&
            (isMobile ? (
              <button
                onClick={handleOpenAnalysis}
                className="flex items-center gap-1.5 bg-gray-800 text-white px-3 py-1.5 rounded-md text-sm font-medium hover:bg-gray-700 transition-colors"
              >
                分析を表示
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            ) : (
              <div
                className="show-analysis-sm p-1 bg-green-500 w-[120px] flex align-center items-center text-white rounded-2xl cursor-pointer justify-center"
                onClick={handleOpenAnalysis}
              >
                分析画面を開く
              </div>
            ))}
        </div>

        {/* テーブル部分 */}
        <NewReleaseTable
          newReleaseData={newReleaseData?.data}
          onDashboardClick={onDashboardClick}
          selectedCompanies={selectedCompanies}
          onCompanyClick={onCompanyClick}
          addAllCompanies={addAllCompanies}
          addCompany={addCompany}
          removeCompany={removeCompany}
          registeredCompanies={registeredCompanies}
          category={activeTab}
          handleSummaryClick={handleSummaryClick}
          isMobile={isMobile}
        />

        {/* ページネーションコントロール - トータル件数と表示件数を再度表示 */}
        {newReleaseData?.data && newReleaseData.data.length > 0 && (
          <div className="flex justify-between items-center mt-4 px-4 py-2">
            <div className="text-sm text-gray-600">
              全 {totalItems} 件中 {startItem} - {endItem} 件表示
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={goToPreviousPage}
                disabled={currentPage === 1}
                className={`flex items-center gap-1 px-4 py-2 rounded-md ${
                  currentPage === 1
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                <ChevronLeft className="h-4 w-4" />
                前へ
              </button>
              <button
                onClick={goToNextPage}
                disabled={currentPage === totalPages}
                className={`flex items-center gap-1 px-4 py-2 rounded-md ${
                  currentPage === totalPages
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                次へ
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default NewReleaseSearch
